import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Modal, Input } from 'reactstrap';
import { Theme } from '../../../Theme/Theme';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const StyledModal = styled(Modal)`
  .modal-content {
    border: none;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }
`;

export const ModalContent = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
  background: linear-gradient(135deg, rgba(0, 221, 163, 0.08), rgba(0, 112, 99, 0.05));
`;

export const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const TitleIcon = styled.span`
  font-size: 20px;
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${Theme.Colors.lightestGrey};
  color: ${Theme.Colors.charcoal};
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${Theme.Colors.lighterGrey};
    transform: scale(1.1);
  }
`;

export const ModalBody = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  max-height: 65vh;
  overflow: hidden;
`;

export const SearchSection = styled.div`
  padding: 20px 24px 16px;
  flex-shrink: 0;
`;

export const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;
`;

export const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid ${Theme.Colors.lighterGrey};
  background: ${Theme.Colors.lightestGrey}50;
`;

export const SearchContainer = styled.div`
  position: relative;
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${Theme.Colors.warmGrey};
  font-size: 16px;
  pointer-events: none;
`;

export const SearchInput = styled(Input)`
  padding: 14px 16px 14px 44px;
  border-radius: 14px;
  font-size: 15px;
  border: 2px solid ${Theme.Colors.lighterGrey};
  background: ${Theme.Colors.lightestGrey};
  transition: all 0.2s ease;

  &:focus {
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 4px ${Theme.Colors.primary}15;
    background: white;
  }

  &::placeholder {
    color: ${Theme.Colors.warmGrey};
  }
`;

export const SectionTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: ${Theme.Colors.warmGrey};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
`;

export const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const LoadMoreTrigger = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  min-height: 50px;
`;

export const ActivityItem = styled.button<{ $delay?: number }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 2px solid ${Theme.Colors.lighterGrey};
  border-radius: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
  animation: ${slideIn} 0.3s ease-out forwards;
  animation-delay: ${({ $delay }) => ($delay || 0) * 30}ms;
  opacity: 0;

  &:hover {
    border-color: ${Theme.Colors.primary};
    background: ${Theme.Colors.primary}08;
    transform: translateX(4px);
    box-shadow: 0 4px 12px ${Theme.Colors.primary}15;
  }
`;

export const ActivityImage = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: ${Theme.Colors.lightestGrey};
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ActivityInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ActivityName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ActivityCategory = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.charcoal};
  margin-top: 2px;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

export const NoResults = styled.div`
  text-align: center;
  padding: 24px;
  color: ${Theme.Colors.charcoal};
  font-size: 14px;
`;

export const EditContainer = styled.div`
  padding: 8px 0;
`;

export const SelectedActivityHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 12px;
  margin-bottom: 24px;
`;

export const SelectedActivityImage = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 10px;
  background: white;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const SelectedActivityInfo = styled.div`
  flex: 1;
`;

export const SelectedActivityName = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  color: ${Theme.Colors.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    gap: 8px;
  }
`;

export const PrimaryButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  border-radius: 12px;
  border: none;
  background: #00dda2;
  color: white;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 221, 163, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #007763;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 112, 99, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  padding: 14px 24px;
  border-radius: 12px;
  border: 2px solid ${Theme.Colors.lighterGrey};
  background: white;
  color: ${Theme.Colors.textColor};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${Theme.Colors.charcoal};
    background: ${Theme.Colors.lightestGrey};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const FormSection = styled.div`
  margin-bottom: 24px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${Theme.Colors.charcoal};
  margin-bottom: 6px;
`;

export const FormInput = styled(Input)`
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 15px;
  border: 1px solid ${Theme.Colors.lighterGrey};

  &:focus {
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 3px ${Theme.Colors.primary}20;
  }
`;

export const TrackingKeyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};

  &:last-child {
    border-bottom: none;
  }
`;

export const TrackingKeyIcon = styled.div<{ $color: string; $bgColor: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${(props) => props.$bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 18px;
    color: ${(props) => props.$color};
  }
`;

export const TrackingKeyLabel = styled.div`
  flex: 1;
  font-size: 15px;
  color: ${Theme.Colors.textColor};
`;

export const TrackingKeyInput = styled(Input)`
  width: 100px;
  text-align: right;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  border: 1px solid ${Theme.Colors.lighterGrey};

  &:focus {
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 3px ${Theme.Colors.primary}20;
  }
`;

export const TrackingKeyUnit = styled.span`
  font-size: 14px;
  color: ${Theme.Colors.warmGrey};
  min-width: 50px;
`;
